import { useEffect, useRef } from 'react';
import { playRefill } from '@goodtime/engine';
import coinIconUrl from '../assets/icon/coin.png';
import chestUrl from '../assets/reel/shared/coin_chest.png';

/**
 * RefillBurst: the celebration when a player's coins run out and the game
 * tops them up for free, the brand's central promise ("Infinite Coins ·
 * All the spin, none of the sting"). The screen dims warm, a chest of
 * coins bursts open, the counter rolls up to the new balance, the line
 * says it's on the house and the coins fly into the coin counter.
 *
 * <p>Drawn in Pixi on the overlay canvas over any screen (engine
 * `playRefill`; producer 2026-10-14: everything in the game in Pixi). It
 * takes no taps. Renders nothing itself, so it's cheap to keep mounted at
 * the App root.
 *
 * <p>Triggered by the {@code active} prop on a false to true edge; hold it
 * true through the moment, and clear it in {@code onComplete}.
 */
export interface RefillBurstProps {
  /** False to true starts a refill. Hold true through it; flip to false
   *  to disarm before the next. */
  active: boolean;
  /** The counter starts here: almost always 0. */
  fromBalance?: number;
  /** What the refill added; the counter rolls to fromBalance + amount. */
  amount?: number;
  /** Where the coin counter is on screen, for the coins to fly into. */
  target?: () => { x: number; y: number } | null;
  /** Fires once the moment has gone. */
  onComplete?: () => void;
}

const ART = { coin: coinIconUrl, chest: chestUrl };

export function RefillBurst({ active, fromBalance = 0, amount = 1000, target, onComplete }: RefillBurstProps) {
  const latest = useRef({ fromBalance, amount, target, onComplete });
  latest.current = { fromBalance, amount, target, onComplete };
  useEffect(() => {
    if (!active) return;
    const now = latest.current;
    void playRefill({
      art: ART,
      from: now.fromBalance,
      amount: now.amount,
      target: () => latest.current.target?.() ?? null,
    }).then(() => latest.current.onComplete?.());
  }, [active]);
  return null;
}
